import { useState, useRef, useCallback, useEffect } from 'react';

import { DropdownItem } from './Dropdown.types';

type UseDropdownProps<T extends string> = {
    items: DropdownItem<T>[];
    onSelect: (item: DropdownItem<T>) => void;
    autoCloseOnTriggerHidden: boolean;
    multiply?: boolean;
    selectedValues?: (string | null | undefined)[];
    disabled?: boolean;
};

/**
 * Хук для автоматического закрытия dropdown при скрытии триггера за пределы экрана
 */
export const useDropdownVisibilityClose = (
    triggerRef: React.RefObject<HTMLButtonElement | null>,
    isOpen: boolean,
    onClose: () => void,
) => {
    useEffect(() => {
        if (!isOpen || !triggerRef.current) {
            return;
        }

        const observer = new IntersectionObserver(
            entries => {
                const [entry] = entries;

                // Если триггер не виден на экране, закрываем dropdown
                if (!entry.isIntersecting) {
                    onClose();
                }
            },
            {
                // Порог видимости - элемент считается невидимым, если видно менее 10%
                threshold: 0.1,
                // Учитываем весь viewport
                rootMargin: '0px',
            },
        );

        observer.observe(triggerRef.current);

        return () => {
            observer.disconnect();
        };
    }, [isOpen, triggerRef, onClose]);
};

export const useDropdown = <T extends string>({
    items,
    onSelect,
    autoCloseOnTriggerHidden,
    multiply = false,
    selectedValues = [],
    disabled = false,
}: UseDropdownProps<T>) => {
    const [isOpen, setIsOpen] = useState(false);

    const dropdownRef = useRef<HTMLDivElement>(null);
    const triggerRef = useRef<HTMLButtonElement>(null);

    // Фильтруем активные элементы (не отключенные)
    const activeItems = items.filter(item => !item.disabled);

    // Проверяем, выбран ли элемент
    const isItemSelected = useCallback(
        (item: DropdownItem<T>) => {
            return selectedValues.includes(item.value);
        },
        [selectedValues],
    );

    const handleToggle = useCallback(() => {
        if (disabled) {
            return;
        }

        setIsOpen(prev => !prev);
    }, [disabled]);

    const handleSelect = useCallback(
        (item: DropdownItem<T>) => {
            if (item.disabled) {
                return;
            }

            onSelect(item);

            // Для одиночного выбора закрываем dropdown
            if (!multiply) {
                setIsOpen(false);
            }
        },
        [onSelect, multiply],
    );

    const handleClose = useCallback(() => {
        setIsOpen(false);
    }, []);

    // Используем хук для автоматического закрытия при скрытии триггера
    useDropdownVisibilityClose(triggerRef, isOpen && autoCloseOnTriggerHidden, handleClose);

    return {
        isOpen,
        activeItems,
        dropdownRef,
        triggerRef,
        handleToggle,
        handleSelect,
        setIsOpen,
        isItemSelected,
    };
};
